import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import DashHeader from "../../components/DashHeader";
import { fetchWithTokenRefresh } from "../../utils/fetchWithTokenRefresh";
import { triggerCreditUpdate } from "../../utils/creditManager";
import ImageTaskbar from "./ImageTaskbar";
import ImageBento from "./ImageBento";

const Images = () => {
  const [inputValue, setInputValue] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("realistic");
  const [images, setImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage] = useState(16);
  const [totalImages, setTotalImages] = useState(0);
  const [processingJobId, setProcessingJobId] = useState(null);
  const intervalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", checkIfMobile);
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const handleCreate = async (aspectRatio = "1:1") => {
    if (!inputValue.trim()) {
      alert("Please enter a prompt.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithTokenRefresh(
        "https://api.vidstudio.ai/trigger-pic",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            input: inputValue,
            style: selectedStyle,
            aspectRatio: aspectRatio
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        triggerCreditUpdate();
        
        setProcessingJobId(data.jobId);
        setImages(prevImages => [{
          jobId: data.jobId,
          status: 'Processing',
          startedAt: new Date().toISOString(),
          inputPrompt: inputValue,
          style: selectedStyle,
          aspectRatio: aspectRatio
        }, ...prevImages]);
        
        if (data.jobId) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          intervalRef.current = setInterval(() => {
            pollImageStatus(data.jobId);
          }, 1000);
        }
        
        setInputValue("");
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error("Error creating:", error);
      alert("Failed to create image.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchImages = async (page = 1, limit = 16) => {
    try {
      const response = await fetchWithTokenRefresh(
        `https://api.vidstudio.ai/user/images?page=${page}&limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setImages(prevImages => {
          const imagesMap = new Map();
          prevImages.forEach(img => imagesMap.set(img.jobId, img));
          data.images.forEach(img => imagesMap.set(img.jobId, img));
          return Array.from(imagesMap.values()).sort((a, b) => 
            new Date(b.startedAt) - new Date(a.startedAt)
          );
        });
        setTotalImages(data.total);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const pollImageStatus = async (jobId) => {
    try {
      const response = await fetchWithTokenRefresh(
        `https://api.vidstudio.ai/user/images?page=1&limit=${imagesPerPage}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();
      
      if (response.ok) {
        const updatedImage = data.images.find(img => img.jobId === jobId);
        
        if (updatedImage) {
          setImages(prevImages => {
            const updatedImages = prevImages.map(img => 
              img.jobId === jobId ? updatedImage : img
            );
            return updatedImages.sort((a, b) => 
              new Date(b.startedAt) - new Date(a.startedAt)
            );
          });
          
          if (updatedImage.status !== 'Processing') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            setProcessingJobId(null);
          }
        }
      }
    } catch (error) {
      console.error('Error polling image status:', error);
    }
  };

  useEffect(() => {
    fetchImages(currentPage, imagesPerPage);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentPage, imagesPerPage]);

  const handleLoadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashHeader />
        <main className="flex-1 overflow-y-auto">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="flex flex-col gap-8">
                <ImageTaskbar
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  selectedStyle={selectedStyle}
                  setSelectedStyle={setSelectedStyle}
                  handleCreate={handleCreate}
                  isLoading={isLoading}
                />
                <ImageBento
                  images={images}
                  currentPage={currentPage}
                  imagesPerPage={imagesPerPage}
                  totalImages={totalImages}
                  handleLoadMore={handleLoadMore}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Images;